import { Alert, Box, Button, Paper, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { JSONTree } from 'react-json-tree';
import { DeviceSensorValues } from '../components/DeviceViewComponents/DeviceSensorValues';
import { StreamVideo } from '../components/DeviceViewComponents/Video/ShowCameras';
import { ToggleCameraView } from '../components/DeviceViewComponents/Video/ToggleCameraView';
import Graph from '../components/Graph';
import LogViewer from '../components/LogViewer';
import { videoStreamingApi } from '../services/backend/backendApi';
import { isDesktop } from '../services/backend/isDesktop.js';
import { useDeviceStore } from '../stores/deviceStore';
import { useFileDataStore } from '../stores/fileDataStore';
import { useNavigationStore } from '../stores/navigationStore';
import { useProjectStore } from '../stores/projectStore';

const jsonTreeTheme = {
  scheme: 'mui',
  base00: '#fafafa',
  base01: '#f5f5f5',
  base02: '#eeeeee',
  base03: '#9e9e9e',
  base04: '#757575',
  base05: '#212121',
  base06: '#212121',
  base07: '#212121',
  base08: '#d32f2f',
  base09: '#e65100',
  base0A: '#f9a825',
  base0B: '#2e7d32',
  base0C: '#00838f',
  base0D: '#1565c0',
  base0E: '#6a1b9a',
  base0F: '#d32f2f',
};

/**
 * Shown in the browser build, where there is no device to talk to.
 */
const DesktopRequiredNotice = () => (
  <Box
    sx={{
      p: 2,
      height: 'calc(100vh - 40px)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Typography variant="h6">Desktop environment required to view this page</Typography>
  </Box>
);

/**
 * The real device view. Only mounted on desktop, so every hook below runs on
 * every render - React requires hooks to be called in the same order each time,
 * which an early `return` in the middle of the component would break.
 */
const DeviceViewDesktop = () => {
  // Device store selectors
  const endpointUrl = useDeviceStore((state) => state.endpointUrl);
  const setEndpointUrl = useDeviceStore((state) => state.setEndpointUrl);
  const connectionState = useDeviceStore((state) => state.connectionState);
  const connectionError = useDeviceStore((state) => state.connectionError);
  const deviceDescriptor = useDeviceStore((state) => state.deviceDescriptor);
  const selectedTab = useDeviceStore((state) => state.selectedTab);
  const setSelectedTab = useDeviceStore((state) => state.setSelectedTab);
  const connect = useDeviceStore((state) => state.connect);
  const disconnect = useDeviceStore((state) => state.disconnect);
  const fetchDescriptor = useDeviceStore((state) => state.fetchDescriptor);
  const clearData = useDeviceStore((state) => state.clearData);
  const setConnectionError = useDeviceStore((state) => state.setConnectionError);
  const chamberAData = useDeviceStore((state) => state.chamberAData);
  const currentFileMeta = useDeviceStore((state) => state.currentFileMeta);

  // File data store actions (for Graph component)
  const setChamberData = useFileDataStore((state) => state.setChamberData);

  // Navigation store (for Graph active state)
  const setActivePlot = useNavigationStore((state) => state.setActivePlot);
  const activePlotId = useNavigationStore((state) => state.activePlotId);

  const currentProjectId = useProjectStore((state) => state.currentProjectId);
  const hasChamberData = useDeviceStore((state) => !!state.chamberData[state.selectedTab]);
  const isConnected = connectionState === 'connected';
  const isConnecting = connectionState === 'connecting';

  const NUM_CHAMBERS = 12;

  // Real sensor reference IDs are only known once connected. They route the
  // live data (chamberData is keyed by referenceId) and label the tabs.
  const connectedChamberIds =
    isConnected && deviceDescriptor?.o2Controllers
      ? deviceDescriptor.o2Controllers.map((ctrl) => ctrl.o2Sensor?.referenceId).filter(Boolean)
      : [];

  // Chamber tabs are always rendered (12 of them). Before a measurement
  // starts they are placeholders (chamber-1..chamber-12); once connected they
  // switch to the device's real reference IDs so the live graph data lines up.
  const chamberTabs =
    connectedChamberIds.length > 0
      ? connectedChamberIds.map((id) => ({ value: id, label: id.split('-').pop() }))
      : Array.from({ length: NUM_CHAMBERS }, (_, i) => ({
          value: `chamber-${i + 1}`,
          label: `${i + 1}`,
        }));

  const tabValues = [...chamberTabs.map((t) => t.value), 'device', 'logs'];
  // Guard against MUI's "value not in tabs" warning during the brief render
  // where selection is still a placeholder but tabs have become real IDs.
  const safeSelectedTab = tabValues.includes(selectedTab)
    ? selectedTab
    : (chamberTabs[0]?.value ?? 'logs');
  const [urlInput, setUrlInput] = useState(endpointUrl);
  const [relayIp, setRelayIp] = useState('');
  const [relayUsername, setRelayUsername] = useState('');
  const [relayPassword, setRelayPassword] = useState('');
  const [localIp, setLocalIp] = useState('');
  const [showVideo, setShowVideo] = useState(false);

  // Sync device store data to file data store for Graph component
  useEffect(() => {
    if (chamberAData && isConnected && selectedTab !== 'logs' && selectedTab !== 'device') {
      // Sync chamberAData to fileDataStore so Graph can read it
      setChamberData(
        chamberAData,
        null,
        currentFileMeta || { file_name: 'Device', data_frequency: 10 },
      );
    }
  }, [chamberAData, isConnected, selectedTab, currentFileMeta, setChamberData]);

  // Set this plot as active when showing graph
  useEffect(() => {
    if (isConnected && hasChamberData && selectedTab !== 'logs' && selectedTab !== 'device') {
      setActivePlot(selectedTab, 1);
    } else {
      // Clear active plot when not showing graph
      if (activePlotId === 'A') {
        setActivePlot(null);
      }
    }
  }, [isConnected, hasChamberData, selectedTab, setActivePlot, activePlotId]);

  // Cleanup: clear active plot on unmount
  useEffect(() => {
    return () => {
      if (activePlotId === 'A') {
        setActivePlot(null);
      }
    };
  }, [activePlotId, setActivePlot]);

  // When a measurement starts, chamber tabs switch from placeholders
  // (chamber-N) to real reference IDs. Remap the current selection onto the
  // same chamber number so the user stays where they were.
  const connectedChamberKey = connectedChamberIds.join(',');
  useEffect(() => {
    if (connectedChamberIds.length === 0) return;
    if (selectedTab === 'logs' || selectedTab === 'device') return;
    if (connectedChamberIds.includes(selectedTab)) return;
    const match = /^chamber-(\d+)$/.exec(selectedTab);
    const index = match ? Number(match[1]) - 1 : 0;
    setSelectedTab(connectedChamberIds[index] || connectedChamberIds[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedChamberKey, selectedTab, setSelectedTab]);

  const handleConnect = async () => {
    setEndpointUrl(urlInput);
    try {
      await fetchDescriptor(relayIp, relayUsername, relayPassword, localIp);
      connect(currentProjectId);
    } catch (e) {
      setConnectionError(`Failed to fetch device descriptor: ${e.message}`);
    }
  };

  const handleDisconnect = () => {
    disconnect();
    clearData();
    // Clear file data store when disconnecting
    setChamberData(null, null, null);
  };

  useEffect(() => {
    if (!showVideo) {
      videoStreamingApi.stop().catch((err) => {
        console.error('Failed to stop video streaming:', err);
      });
    }
  }, [showVideo]);

  return (
    <>
      <Box sx={{ p: 2, height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column' }}>
        {/* ... existing header and connection UI ... */}
        <Stack direction="row" alignItems="center" gap={2} mb={2}>
          <Typography variant="h6">12k Device</Typography>
          {isConnected && deviceDescriptor && (
            <Typography variant="caption" color="text.secondary">
              {deviceDescriptor.productionInformation?.serialNumber}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" gap={1} mb={2}>
          <Stack direction="column" gap={1} sx={{ flex: 1 }}>
            <TextField
              size="small"
              label="Device WebSocket URL"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              disabled={isConnected || isConnecting}
              placeholder="ws://10.12.15.20:5002/ws"
            />
            <Stack direction="row" gap={1}>
              <TextField
                size="small"
                label="Relay Device IP Address (tailscale)"
                value={relayIp}
                onChange={(e) => setRelayIp(e.target.value)}
                disabled={isConnected || isConnecting}
                sx={{ flex: 1 }}
                placeholder="100.117.136.105"
              />
              <TextField
                size="small"
                label="Relay device Username"
                value={relayUsername}
                onChange={(e) => setRelayUsername(e.target.value)}
                disabled={isConnected || isConnecting}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Relay device Password"
                type="password"
                value={relayPassword}
                onChange={(e) => setRelayPassword(e.target.value)}
                disabled={isConnected || isConnecting}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Local IP (interface to 12k)"
                value={localIp}
                onChange={(e) => setLocalIp(e.target.value)}
                disabled={isConnected || isConnecting}
                sx={{ flex: 1 }}
                placeholder="10.12.15.21"
              />
            </Stack>
          </Stack>
          {isConnected ? (
            <Button variant="outlined" color="error" onClick={handleDisconnect}>
              Stop Measurement
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleConnect}
              disabled={isConnecting || !urlInput || !currentProjectId}
              sx={{ color: '#ffffff' }}
            >
              {isConnecting ? 'Starting...' : 'Start Measurement'}
            </Button>
          )}
        </Stack>

        {!currentProjectId && !isConnected && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Select a project in the Project Explorer before connecting.
          </Alert>
        )}
        {connectionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {connectionError}
          </Alert>
        )}
        <Tabs
          value={safeSelectedTab}
          onChange={(e, v) => setSelectedTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          {chamberTabs.map((t) => (
            <Tab key={t.value} label={t.label} value={t.value} />
          ))}
          <Tab label="Device" value="device" />
          <Tab label="Logs" value="logs" />
        </Tabs>
        <DeviceSensorValues />

        {selectedTab === 'logs' ? (
          <LogViewer sx={{ flex: 1 }} />
        ) : selectedTab === 'device' ? (
          isConnected && deviceDescriptor ? (
            <Paper sx={{ flex: 1, p: 2, overflow: 'auto', boxShadow: 'none' }}>
              <Box sx={{ fontSize: 13, fontFamily: 'monospace' }}>
                <JSONTree
                  data={deviceDescriptor}
                  theme={jsonTreeTheme}
                  invertTheme={false}
                  hideRoot
                  shouldExpandNodeInitially={(keyPath) =>
                    keyPath.length === 0 || keyPath[0] === 'productionInformation'
                  }
                />
              </Box>
            </Paper>
          ) : (
            <Paper
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#D8DCDF',
                boxShadow: 'none',
              }}
            >
              <Typography color="text.secondary">
                Click 'Start Measurement' to view device information
              </Typography>
            </Paper>
          )
        ) : isConnected && hasChamberData ? (
          <>
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                width: '100%',
                flexDirection: 'row',
                gap: 1,
                alignItems: 'top',
                justifyContent: 'center',
              }}
            >
              <Paper sx={{ flex: 1, minHeight: 0 }}>
                <Graph key={selectedTab} chamber="A" isActive={activePlotId === 'A'} />
              </Paper>
              {showVideo && <StreamVideo chamberNumber={selectedTab} />}
            </Box>
            <ToggleCameraView showVideo={showVideo} setShowVideo={setShowVideo} />
          </>
        ) : (
          <Paper
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#D8DCDF',
              boxShadow: 'none',
            }}
          >
            <Typography color="text.secondary">
              {isConnected
                ? 'Waiting for measurement data...'
                : "Click 'Start Measurement' to start a new measurement"}
            </Typography>
          </Paper>
        )}
      </Box>
    </>
  );
};

// `isDesktop` is decided once at module load, so this picks one branch for the
// whole session and the hooks inside DeviceViewDesktop never change order.
export const DeviceView = () => (isDesktop ? <DeviceViewDesktop /> : <DesktopRequiredNotice />);
